export interface RazorpayCheckoutHandlerResponse {
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

interface RazorpayCheckoutFailure {
  error?: {
    code?: string;
    description?: string;
  };
}

interface RazorpayCheckoutOptions {
  key: string;
  order_id: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  prefill?: {
    email?: string;
    name?: string;
  };
  notes?: Record<string, string>;
  modal?: {
    ondismiss?: () => void;
  };
  handler?: (response: RazorpayCheckoutHandlerResponse) => void;
  theme?: {
    color: string;
  };
}

interface RazorpayCheckoutInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (response: RazorpayCheckoutFailure) => void) => void;
}

interface RazorpayCheckoutConstructor {
  new (options: RazorpayCheckoutOptions): RazorpayCheckoutInstance;
}

type RazorpayWindow = Window & {
  Razorpay?: RazorpayCheckoutConstructor;
};

let razorpayCheckoutScriptPromise: Promise<void> | null = null;

export function loadRazorpayCheckoutScript(): Promise<void> {
  const razorpayWindow = window as RazorpayWindow;

  if (razorpayWindow.Razorpay) {
    return Promise.resolve();
  }

  if (razorpayCheckoutScriptPromise) {
    return razorpayCheckoutScriptPromise;
  }

  razorpayCheckoutScriptPromise = new Promise<void>((resolve, reject) => {
    const scriptSrc = 'https://checkout.razorpay.com/v1/checkout.js';
    const existing = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existing) {
      existing.remove();
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay Checkout SDK'));

    document.body.appendChild(script);
  });

  return razorpayCheckoutScriptPromise;
}

export async function openRazorpayOrderCheckout(input: {
  keyId: string;
  orderId: string;
  amountSubunits: number;
  currency: string;
  name: string;
  description: string;
  prefill?: {
    email?: string;
    name?: string | null;
  };
  notes?: Record<string, string>;
}): Promise<RazorpayCheckoutHandlerResponse> {
  const razorpayWindow = window as RazorpayWindow;
  await loadRazorpayCheckoutScript();

  const RazorpayCtor = razorpayWindow.Razorpay;
  if (!RazorpayCtor) {
    throw new Error('Razorpay Checkout SDK failed to initialize');
  }

  return new Promise<RazorpayCheckoutHandlerResponse>((resolve, reject) => {
    const checkout = new RazorpayCtor({
      key: input.keyId,
      order_id: input.orderId,
      amount: input.amountSubunits,
      currency: input.currency,
      name: input.name,
      description: input.description,
      prefill: {
        email: input.prefill?.email,
        name: input.prefill?.name || undefined,
      },
      notes: input.notes,
      theme: {
        color: '#10B981',
      },
      modal: {
        ondismiss: () => reject(new Error('Checkout closed before payment completion.')),
      },
      handler: (response) => resolve(response),
    });

    checkout.on('payment.failed', (response) => {
      const code = response.error?.code ? ` (${response.error.code})` : '';
      reject(new Error((response.error?.description || 'Payment failed') + code));
    });

    checkout.open();
  });
}
